// ErrorBoundary.tsx
import React from "react";

type Props = {
  children: React.ReactNode;
  fallback?: (args: { error: unknown; reset: () => void }) => React.ReactNode;
  onError?: (error: unknown, info: React.ErrorInfo) => void;
};

type State = { hasError: boolean; error: unknown; isChunkError: boolean };

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, error: null, isChunkError: false };

  static getDerivedStateFromError(error: unknown) {
    const isChunkError = (error as any)?.name === 'ChunkLoadError';
    return { hasError: true, error, isChunkError };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    this.props.onError?.(error, info); // send to Sentry/NewRelic, etc.
  }

  reset = () => this.setState({ hasError: false, error: null, isChunkError: false });

  render() {
    if (this.state.hasError) {
      // If HTML banner already handled a chunk error, suppress React rendering
      if (this.state.isChunkError && (window as any).bundleErrorShown) {
        return null;
      }

      // If it's a chunk loading error and no custom fallback provided, show refresh UI
      if (this.state.isChunkError && !this.props.fallback) {
        return (
          <div role="alert" style={{ padding: 16, textAlign: 'center' }}>
            <h3>Loading Failed — A New Version Is Available</h3>
            <p style={{ marginBottom: 16 }}>Please refresh the page to load the latest version.</p>
            <button
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: '#0066cc',
                color: 'white',
                border: 'none',
                padding: '8px 20px',
                fontSize: '14px',
                fontWeight: 600,
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Refresh Now
            </button>
          </div>
        );
      }
      return this.props.fallback
        ? this.props.fallback({ error: this.state.error, reset: this.reset })
        : (
          <div role="alert" style={{ padding: 16 }}>
            <h3>Something went wrong.</h3>
            <pre style={{ whiteSpace: "pre-wrap" }}>{String(this.state.error)}</pre>
          </div>
        );
    }
    return this.props.children;
  }
}
